/**
 * A campaign's calling hours as ranges per weekday (owner, 2026-10-30: "two or three time spaces like morning and
 * evening … maybe different per day"). Moved from the portal's `apps/web-app/src/api/outbound-view.ts` and
 * `apps/web-app/src/routes/campaigns/calling-hours.ts` (plan-034 VIEW-PKG): only the logic; the weekday words and the
 * form's sentences stay with each app.
 *
 * A range is an ISO weekday (Monday = 1) and `HH:MM` to `HH:MM` in the person's own time zone; an end may be `24:00`,
 * midnight at the end of the day. A campaign stored before ranges existed answers `calling_hours` as `null` and its one
 * window as `day_of_week` with `call_from_local_time`–`call_to_local_time`; {@link callingRangesByWeekdayOf} reads that
 * legacy window as the single range of each of its days.
 */
/** Monday (1) to Sunday (7). */
export const ISO_WEEKDAY = [1, 2, 3, 4, 5, 6, 7];
const START = /^([01]\d|2[0-3]):[0-5]\d$/;
const END = /^(([01]\d|2[0-3]):[0-5]\d|24:00)$/;
/** `HH:MM`, 00:00–23:59: a range's start. */
export function isRangeStart(time) {
    return START.test(time);
}
/** `HH:MM`, 00:00–23:59, or `24:00`: a range's end. */
export function isRangeEnd(time) {
    return END.test(time);
}
/** `HH:MM` (or `24:00`) → minutes past midnight. */
export function minuteOf(time) {
    const [hour, minute] = time.split(":").map(Number);
    return (hour ?? 0) * 60 + (minute ?? 0);
}
/** One window on each of its days, as ranges. */
export function windowRangeOf(dayOfWeek, fromLocalTime, toLocalTime) {
    return [...new Set(dayOfWeek)].sort((a, b) => a - b).map((day) => ({ dayOfWeek: day, fromLocalTime, toLocalTime }));
}
/** The ranges' summary, as the API answers it beside them: the days, the earliest start, the latest end. */
export function windowSummaryOf(hours) {
    const from = hours.map((one) => one.fromLocalTime).sort();
    const to = hours.map((one) => one.toLocalTime).sort();
    return {
        dayOfWeek: [...new Set(hours.map((one) => one.dayOfWeek))].sort((a, b) => a - b),
        callFromLocalTime: from[0] ?? "",
        callToLocalTime: to.at(-1) ?? "",
    };
}
/** The days that have at least one range, ascending. */
export function callingDaysOf(hours) {
    return [...new Set(hours.map((one) => one.dayOfWeek))].sort((a, b) => a - b);
}
/** A day's ranges in minutes, sorted; malformed ones left out (each app's problem text names them). */
export function rangesOfDay(hours, isoDay) {
    return hours
        .filter((one) => one.dayOfWeek === isoDay && START.test(one.fromLocalTime) && END.test(one.toLocalTime))
        .map((one) => ({ from: minuteOf(one.fromLocalTime), to: minuteOf(one.toLocalTime) }))
        .filter((one) => one.from < one.to)
        .sort((a, b) => a.from - b.from);
}
/** The ranges a campaign calls in: its own, or a legacy `null` read as its single window on each of its days. */
export function campaignCallingRangesOf(campaign) {
    if (campaign.callingHours !== null)
        return campaign.callingHours;
    if (campaign.callFromLocalTime === null || campaign.callToLocalTime === null)
        return [];
    return windowRangeOf(campaign.dayOfWeek, campaign.callFromLocalTime, campaign.callToLocalTime);
}
/** Monday to Sunday, each with its ranges by start (a day not called on has none). */
export function callingRangesByWeekdayOf(campaign) {
    const hours = campaignCallingRangesOf(campaign);
    return ISO_WEEKDAY.map((day) => ({
        dayOfWeek: day,
        range: hours.filter((one) => one.dayOfWeek === day).sort((a, b) => a.fromLocalTime.localeCompare(b.fromLocalTime)),
    }));
}
/**
 * The ranges grouped as the portal says them: by day then start, days with identical ranges in one group, the groups
 * in the order of their first day.
 */
export function callingRangeGroupsOf(hours) {
    const byDay = new Map();
    for (const range of [...hours].sort((a, b) => a.dayOfWeek - b.dayOfWeek || a.fromLocalTime.localeCompare(b.fromLocalTime))) {
        const held = byDay.get(range.dayOfWeek) ?? [];
        held.push(range);
        byDay.set(range.dayOfWeek, held);
    }
    const groups = new Map();
    for (const [day, range] of byDay) {
        const key = range.map((one) => `${one.fromLocalTime}–${one.toLocalTime}`).join(", ");
        const held = groups.get(key);
        if (held === undefined)
            groups.set(key, { day: [day], range });
        else
            held.day.push(day);
    }
    return [...groups.values()].map((one) => ({
        dayOfWeek: one.day,
        range: one.range.map((each) => ({ fromLocalTime: each.fromLocalTime, toLocalTime: each.toLocalTime })),
    }));
}
/** The shape of a set of ISO weekdays, ascending (duplicates kept out). */
export function weekdayRunOf(dayOfWeek) {
    const sorted = [...new Set(dayOfWeek)].sort((a, b) => a - b);
    const first = sorted[0];
    const last = sorted.at(-1);
    if (first === undefined || last === undefined)
        return { kind: "none" };
    if (sorted.length === 1)
        return { kind: "one", dayOfWeek: first };
    return sorted.every((one, index) => one === first + index) ? { kind: "run", first, last } : { kind: "spread", dayOfWeek: sorted };
}
//# sourceMappingURL=calling-range.js.map